import { DashboardLayout } from '@/components/layout/DashboardLayout';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, RefreshCw, Loader2, AlertTriangle, DollarSign, FileText,
  CheckCircle2, X, ChevronDown, ChevronUp, Receipt, GitPullRequest,
  ShieldAlert, Clock, TrendingUp, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Cell, PieChart, Pie
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const STATUS_COLORS = {
  DRAFT: '#94A3B8', SUBMITTED: '#3B82F6', UNDER_REVIEW: '#143A6B', MEASURED: '#6366F1',
  CERTIFIED: '#0EA5E9', APPROVED: '#22C55E', PAYMENT_RECOMMENDED: '#14B8A6', PAID: '#10B981',
  REJECTED: '#EF4444', ACTIVE: '#22C55E', ON_HOLD: '#F97316', CLOSED: '#94A3B8'
};

function KPICard({ title, value, subtitle, icon: Icon, color = '#0EA5E9' }) {
  return (
    <div className="rounded-xl p-4" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</div>
      {subtitle && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtitle}</div>}
    </div>
  );
}

/* ── Alert Badge ── */
function AlertBadge({ severity }) {
  const cfg = severity === 'CRITICAL'
    ? { bg: '#EF444415', color: '#EF4444', border: '#EF444430', icon: ShieldAlert }
    : { bg: '#F59E0B15', color: '#F59E0B', border: '#F59E0B30', icon: AlertTriangle };
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold"
      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      <Icon className="w-3 h-3" /> {severity}
    </span>
  );
}

/* ── Contract Detail Drawer ── */
function ContractDetailDrawer({ contract, onClose, onNavigateInvoice }) {
  if (!contract) return null;
  const c = contract;
  const utilColor = c.utilisationPct > 95 ? '#EF4444' : c.utilisationPct > 80 ? '#F59E0B' : '#22C55E';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="w-full max-w-2xl h-full overflow-y-auto" style={{ backgroundColor: 'var(--card-bg)', borderLeft: '1px solid var(--border-color)' }}
        data-testid="contract-detail-drawer">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 flex items-start justify-between" style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{c.contractNumber}</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${STATUS_COLORS[c.contractStatus] || '#94A3B8'}15`, color: STATUS_COLORS[c.contractStatus] || '#94A3B8' }}>
                {c.contractStatus}
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.contractTitle}</p>
            <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>{c.vendorName} | {(c.contractType || '').replace(/_/g, ' ')} | {c.discipline}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:opacity-70"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Budget Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Budget Utilisation</span>
              <span className="text-sm font-bold" style={{ color: utilColor }}>{c.utilisationPct}%</span>
            </div>
            <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(c.utilisationPct, 100)}%`, backgroundColor: utilColor }} />
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-3 gap-3">
            {[
              ['Original Value', c.originalValue, '#3B82F6'],
              ['Variation Orders', c.variationOrderTotal, '#143A6B'],
              ['Current Value', c.currentValue, '#0EA5E9'],
              ['Invoiced', c.totalInvoiced, '#F97316'],
              ['Paid', c.totalPaid, '#22C55E'],
              ['Outstanding', c.outstanding, '#EF4444'],
            ].map(([label, val, color]) => (
              <div key={label} className="rounded-lg p-3" style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                <div className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{label}</div>
                <div className="text-sm font-bold" style={{ color }}>{fmt(val)}</div>
              </div>
            ))}
          </div>

          {/* Invoices */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4" style={{ color: '#0EA5E9' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Invoices ({c.invoiceCount})</h3>
            </div>
            {c.invoices?.length > 0 ? (
              <div className="space-y-2">
                {c.invoices.map(inv => {
                  const sc = STATUS_COLORS[inv.status] || '#94A3B8';
                  return (
                    <div key={inv.internalRef} className="rounded-lg p-3 flex items-center gap-3"
                      style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold" style={{ color: '#0EA5E9' }}>{inv.internalRef}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${sc}15`, color: sc, border: `1px solid ${sc}30` }}>
                            {(inv.status || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{inv.invoiceNumber} | {(inv.invoiceDate || '').slice(0, 10)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{fmt(inv.grossValue)}</div>
                        <div className="text-[10px]" style={{ color: '#22C55E' }}>Paid: {fmt(inv.paid)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs py-3 text-center" style={{ color: 'var(--text-disabled)' }}>No invoices for this contract</p>
            )}
          </div>

          {/* Change Orders */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <GitPullRequest className="w-4 h-4" style={{ color: '#143A6B' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Change Orders ({c.coCount})</h3>
            </div>
            {c.changeOrders?.length > 0 ? (
              <div className="space-y-2">
                {c.changeOrders.map((co, i) => (
                  <div key={i} className="rounded-lg p-3 flex items-center gap-3"
                    style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                    <div className="flex-1">
                      <div className="text-xs font-semibold" style={{ color: '#143A6B' }}>{co.changeOrderNumber}</div>
                      <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{co.title}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium" style={{ color: co.costImpact >= 0 ? '#EF4444' : '#22C55E' }}>
                        {co.costImpact >= 0 ? '+' : ''}{fmt(co.costImpact)}
                      </div>
                      <div className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{co.status} | {co.source?.replace(/_/g, ' ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs py-3 text-center" style={{ color: 'var(--text-disabled)' }}>No change orders linked to this contract</p>
            )}
          </div>

          {/* Variation Orders */}
          {c.variationOrders?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4" style={{ color: '#F59E0B' }} />
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Variation Orders ({c.voCount})</h3>
              </div>
              <div className="space-y-2">
                {c.variationOrders.map((vo, i) => (
                  <div key={i} className="rounded-lg p-3 flex items-center gap-3"
                    style={{ backgroundColor: 'var(--app-bg)', border: '1px solid var(--border-color)' }}>
                    <div className="flex-1">
                      <div className="text-xs font-semibold" style={{ color: '#F59E0B' }}>{vo.voNumber}</div>
                      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{vo.voTitle}</div>
                    </div>
                    <div className="text-xs font-medium" style={{ color: vo.voValue >= 0 ? '#EF4444' : '#22C55E' }}>
                      {vo.voValue >= 0 ? '+' : ''}{fmt(vo.voValue)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Navigation */}
          <div className="pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
            <Button variant="outline" size="sm" className="mr-2" onClick={() => { onClose(); onNavigateInvoice(c.contractNumber); }}
              data-testid="nav-to-invoices-btn">
              <Receipt className="w-3.5 h-3.5 mr-1" /> View in Invoice Management
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function CommitmentTracking() {
  const { projectId: ctxProjectId } = useProject();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => {
      setProjects(r.data.projects || []);
      if (r.data.projects?.length > 0) setSelectedProject(r.data.projects[0].project_id);
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${selectedProject}/cc/commitment-tracking`);
      setData(res.data);
    } catch {
      toast.error('Failed to load commitment data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const summary = data?.summary || {};
  const alerts = data?.alerts || [];

  /* Top 10 contracts by current value for chart */
  const chartData = useMemo(() => {
    if (!data?.contracts) return [];
    return [...data.contracts]
      .sort((a, b) => b.currentValue - a.currentValue)
      .slice(0, 10)
      .map(c => ({
        name: c.contractNumber,
        vendor: c.vendorName,
        original: c.originalValue,
        variations: c.variationOrderTotal,
        invoiced: c.totalInvoiced,
        paid: c.totalPaid,
        remaining: Math.max(0, c.currentValue - c.totalInvoiced),
      }));
  }, [data]);

  /* Utilisation distribution for donut */
  const utilDistribution = useMemo(() => {
    if (!data?.contracts) return [];
    const buckets = { 'Over 95%': 0, '80-95%': 0, '50-80%': 0, 'Under 50%': 0 };
    data.contracts.forEach(c => {
      if (c.utilisationPct > 95) buckets['Over 95%']++;
      else if (c.utilisationPct > 80) buckets['80-95%']++;
      else if (c.utilisationPct > 50) buckets['50-80%']++;
      else buckets['Under 50%']++;
    });
    return [
      { name: 'Over 95%', value: buckets['Over 95%'], color: '#EF4444' },
      { name: '80-95%', value: buckets['80-95%'], color: '#F59E0B' },
      { name: '50-80%', value: buckets['50-80%'], color: '#3B82F6' },
      { name: 'Under 50%', value: buckets['Under 50%'], color: '#22C55E' },
    ].filter(b => b.value > 0);
  }, [data]);

  /* Filtered contracts for grid */
  const filteredContracts = useMemo(() => {
    if (!data?.contracts) return [];
    if (!searchText) return data.contracts;
    const q = searchText.toLowerCase();
    return data.contracts.filter(c =>
      (c.contractNumber || '').toLowerCase().includes(q) || 
      (c.vendorName || '').toLowerCase().includes(q) ||
      (c.costCode || '').toLowerCase().includes(q) || 
      (c.discipline || '').toLowerCase().includes(q)
    );
  }, [data, searchText]);

  /* AG Grid column definitions */
  const columnDefs = useMemo(() => [
    { field: 'contractNumber', headerName: 'Contract', width: 95, pinned: 'left', cellStyle: { fontWeight: 600, color: '#0EA5E9', cursor: 'pointer' } },
    { field: 'vendorName', headerName: 'Vendor', flex: 1, minWidth: 150 },
    { field: 'discipline', headerName: 'Discipline', width: 110 },
    { field: 'originalValue', headerName: 'Original', width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value) },
    { field: 'variationOrderTotal', headerName: 'VOs', width: 85, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: p => ({ color: p.value > 0 ? '#143A6B' : 'var(--text-disabled)' }) },
    { field: 'currentValue', headerName: 'Current', width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: { fontWeight: 600 } },
    { field: 'totalInvoiced', headerName: 'Invoiced', width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: { color: '#F97316' } },
    { field: 'totalPaid', headerName: 'Paid', width: 90, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: { color: '#22C55E' } },
    { field: 'outstanding', headerName: 'O/S', width: 90, type: 'numericColumn', valueFormatter: p => fmt(p.value), cellStyle: p => ({ color: p.value > 0 ? '#EF4444' : 'var(--text-disabled)' }) },
    { field: 'budgetRemaining', headerName: 'Remaining', width: 100, type: 'numericColumn', valueFormatter: p => fmt(p.value),
      cellStyle: p => ({ color: p.value < 0 ? '#EF4444' : p.value < p.data?.currentValue * 0.1 ? '#F59E0B' : '#22C55E', fontWeight: 600 }) },
    { field: 'utilisationPct', headerName: 'Util%', width: 75, type: 'numericColumn', valueFormatter: p => `${p.value}%`,
      cellStyle: p => ({ color: p.value > 95 ? '#EF4444' : p.value > 80 ? '#F59E0B' : '#22C55E', fontWeight: 700 }) },
    { field: 'invoiceCount', headerName: 'Inv#', width: 60, type: 'numericColumn' },
    { field: 'coCount', headerName: 'CO#', width: 55, type: 'numericColumn' },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true, filter: true, resizable: true,
    cellStyle: { fontSize: '12px', display: 'flex', alignItems: 'center' },
  }), []);

  const handleRowClicked = useCallback((e) => {
    setSelectedContract(e.data);
  }, []);

  const handleNavToInvoices = useCallback((contractNumber) => {
    navigate('/project-controls/cost/invoices');
  }, [navigate]);

  return (
    <DashboardLayout>
      {ctxProjectId && <AIAnalystPanel projectId={ctxProjectId} moduleKey="COMMITMENT_TRACKING" defaultOpen={true} compact={true} />}

      <div className="space-y-5" data-testid="commitment-tracking-page">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls/cost')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-2.5 rounded-xl" style={{ background: '#F5920012', border: '1px solid #F5920020' }}>
            <DollarSign className="w-5 h-5" style={{ color: '#F59E0B' }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Commitment Tracking</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Contract-wise budget commitment, spend analysis, and variance alerts</p>
          </div>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm" data-testid="project-selector"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="commitment-kpis">
              <KPICard title="Total Contracts" value={summary.contractCount || 0} icon={FileText} color="#3B82F6" />
              <KPICard title="Total Commitment" value={fmt(summary.totalCurrent)} icon={DollarSign} color="#0EA5E9" subtitle={`Original: ${fmt(summary.totalOriginal)}`} />
              <KPICard title="Total Invoiced" value={fmt(summary.totalInvoiced)} icon={Receipt} color="#F97316" />
              <KPICard title="Total Paid" value={fmt(summary.totalPaid)} icon={CheckCircle2} color="#22C55E" />
              <KPICard title="Outstanding" value={fmt(summary.totalOutstanding)} icon={Clock} color="#EF4444" />
              <KPICard title="Variance Alerts" value={summary.alertCount || 0} icon={AlertTriangle} color={summary.alertCount > 0 ? '#EF4444' : '#22C55E'} />
            </div>

            {/* Alerts */}
            {alerts.length > 0 && (
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="variance-alerts">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <AlertTriangle className="w-4 h-4" style={{ color: '#EF4444' }} /> Variance Alerts
                </h3>
                <div className="space-y-2">
                  {alerts.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: a.severity === 'CRITICAL' ? '#EF444408' : '#F59E0B08', border: `1px solid ${a.severity === 'CRITICAL' ? '#EF444415' : '#F59E0B15'}` }}
                      onClick={() => {
                        const ctr = data.contracts.find(c => c.contractNumber === a.contractNumber);
                        if (ctr) setSelectedContract(ctr);
                      }}
                      data-testid={`alert-${a.contractNumber}`}>
                      <AlertBadge severity={a.severity} />
                      <span className="text-xs font-semibold" style={{ color: '#0EA5E9' }}>{a.contractNumber}</span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{a.vendor}</span>
                      <span className="text-xs flex-1" style={{ color: 'var(--text-secondary)' }}>{a.message}</span>
                      <ExternalLink className="w-3.5 h-3.5" style={{ color: 'var(--text-disabled)' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Commitment vs Spend */}
              <div className="lg:col-span-2 rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="commitment-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Top 10 Contracts — Commitment vs Spend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 10, right: 15, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                      formatter={(v, name) => [fmt(v), name]} labelFormatter={(_, payload) => payload?.[0]?.payload?.vendor || ''} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="original" name="Original" stackId="commitment" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="variations" name="Variation Orders" stackId="commitment" fill="#143A6B" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="invoiced" name="Invoiced" fill="#F97316" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="paid" name="Paid" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Utilisation Donut */}
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="utilisation-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Budget Utilisation</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={utilDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}
                      label={({ name, value }) => `${name}: ${value}`} labelLine={{ stroke: 'var(--text-muted)' }}>
                      {utilDistribution.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {utilDistribution.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                      {d.name}: {d.value}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contract Commitment Grid */}
            <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="commitment-grid-container">
              <div className="p-4 flex items-center gap-3 flex-wrap" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Contract Commitments ({filteredContracts.length})
                </h3>
                <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>Click row for details</span>
                <div className="flex-1" />
                <div className="relative">
                  <Input placeholder="Search contracts..." value={searchText} onChange={e => setSearchText(e.target.value)}
                    className="pl-3 h-8 w-52 text-xs" data-testid="contract-search" />
                </div>
              </div>
              <div className="ag-theme-alpine" style={{ height: 420, width: '100%', '--ag-background-color': 'var(--card-bg)', '--ag-header-background-color': 'var(--table-header-bg)', '--ag-odd-row-background-color': 'var(--table-row-alt)', '--ag-row-hover-color': 'var(--table-row-hover)', '--ag-header-foreground-color': 'var(--text-primary)', '--ag-foreground-color': 'var(--text-primary)', '--ag-border-color': 'var(--border-color)', '--ag-font-size': '12px', '--ag-row-height': '40px', '--ag-header-height': '38px' }}>
                <AgGridReact rowData={filteredContracts} columnDefs={columnDefs} defaultColDef={defaultColDef}
                  animateRows suppressCellFocus pagination paginationPageSize={25}
                  rowSelection="single" onRowClicked={handleRowClicked}
                  getRowId={p => p.data.contractNumber} />
              </div>
            </div>
          </>
        )}

        {/* Contract Detail Drawer */}
        {selectedContract && (
          <ContractDetailDrawer contract={selectedContract}
            onClose={() => setSelectedContract(null)}
            onNavigateInvoice={handleNavToInvoices} />
        )}

        {selectedProject && (
          <ModuleInsightsPanel projectId={selectedProject} moduleKey="commitments" color="#F472B6" deepLinkBase="/project-controls/cost/commitments" />
        )}
      </div>
    </DashboardLayout>
  );
}
